import React, { useState } from "react";
import {
  Col,
  Row,
  Typography,
  Input,
  Button,
  Layout,
  Select,
  InputNumber,
  Card,
  message,
} from "antd";
import { FaArrowLeft } from "react-icons/fa";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { QUESTION } from "../../config/constants/api";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Post } from "../../config/api/post";
import swal from "sweetalert";
import "../upcoming-lessons/upcoming-lessons.css";
import "../course-management/course-management.css";
import "./quiz-details.css";

const { Option } = Select;

const QUESTION_TYPES = [
  { value: "MCQ", label: "MCQ" },
  { value: "TRUE_FALSE", label: "True / False" },
  { value: "FILL_IN_THE_BLANKS", label: "Fill In The Blanks" },
];

const getEmptyQuestion = () => ({
  type: "MCQ",
  title: "",
  score: 1,
  option1: "",
  option2: "",
  option3: "",
  option4: "",
  correctOptionKey: "option1",
  correctOption: "true",
  correctAnswer: "",
});

function QuestionAddBulk() {
  const navigate = useNavigate();
  const token = useSelector((state) => state.user.userToken);
  const { id: quizId } = useParams();
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([getEmptyQuestion()]);

  const addQuestion = () => {
    setQuestions((prev) => [...prev, getEmptyQuestion()]);
  };

  const removeQuestion = (index) => {
    if (questions.length <= 1) {
      message.warning("At least one question is required.");
      return;
    }
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  const updateQuestion = (index, field, value) => {
    setQuestions((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      if (field === "correctOptionKey" && next[index].type === "MCQ") {
        const optKey = value;
        next[index].correctOption = next[index][optKey] ?? "";
      }
      return next;
    });
  };

  const buildPayload = () => {
    const errors = [];
    const payloadQuestions = questions.map((q, i) => {
      const num = i + 1;
      if (!q.title || String(q.title).trim() === "") {
        errors.push(`Question ${num}: Title is required`);
        return null;
      }
      const title = String(q.title).trim();
      const score = q.score != null ? Number(q.score) : 1;

      if (q.type === "MCQ") {
        const options = [q.option1, q.option2, q.option3, q.option4]
          .filter((o) => o != null && String(o).trim() !== "")
          .map((o) => String(o).trim());
        if (options.length < 2) {
          errors.push(`Question ${num}: MCQ must have at least 2 options`);
          return null;
        }
        const correctOption = q[q.correctOptionKey];
        if (correctOption == null || String(correctOption).trim() === "" || !options.includes(String(correctOption).trim())) {
          errors.push(`Question ${num}: Correct option must be one of the options`);
          return null;
        }
        return {
          title,
          type: "MCQ",
          score,
          options,
          correctOption: String(correctOption).trim(),
        };
      }

      if (q.type === "TRUE_FALSE") {
        const co = q.correctOption;
        if (co !== "true" && co !== "false") {
          errors.push(`Question ${num}: True/False must have correctOption "true" or "false"`);
          return null;
        }
        return {
          title,
          type: "TRUE_FALSE",
          score,
          correctOption: co,
        };
      }

      if (q.type === "FILL_IN_THE_BLANKS") {
        if (!q.correctAnswer || String(q.correctAnswer).trim() === "") {
          errors.push(`Question ${num}: Fill in the Blanks must have a correct answer`);
          return null;
        }
        return {
          title,
          type: "FILL_IN_THE_BLANKS",
          score,
          correctAnswer: String(q.correctAnswer).trim(),
        };
      }

      errors.push(`Question ${num}: Invalid question type`);
      return null;
    });

    if (errors.length > 0) {
      message.error(errors[0]);
      return null;
    }

    const filtered = payloadQuestions.filter(Boolean);
    if (filtered.length === 0) return null;

    const titles = new Set();
    for (const q of filtered) {
      if (titles.has(q.title)) {
        message.error("Duplicate question titles in the list are not allowed.");
        return null;
      }
      titles.add(q.title);
    }

    return {
      quiz: quizId,
      questions: filtered,
    };
  };

  const handleSubmit = () => {
    const payload = buildPayload();
    if (!payload) return;
    setLoading(true);
    Post(QUESTION.addBulkQuestion, payload, token, null)
      .then((response) => {
        setLoading(false);
        if (response?.data?.status) {
          swal("Success!", "Questions added successfully", "success");
          navigate(`/quiz-details/${quizId}`);
        } else {
          const msg = response?.data?.message || "Something went wrong";
          const errList = response?.data?.errors;
          swal("Error", errList && errList.length ? errList.join("\n") : msg, "error");
        }
      })
      .catch((e) => {
        setLoading(false);
        const errMsg = e?.response?.data?.message || e?.message || "Request failed";
        const errList = e?.response?.data?.errors;
        swal("Error", errList && errList.length ? errList.join("\n") : errMsg, "error");
      });
  };

  const labelStyle = {
    fontSize: "16px",
    fontWeight: 600,
    color: "black",
    textAlign: "left",
    marginBottom: 20,
  };
  const colLabelProps = {
    style: {
      display: "flex",
      alignItems: "flex-start",
      flexDirection: "column",
    },
  };

  return (
    <Layout
      className="configuration upcoming-lessons-page quiz-details-page"
      style={{ minHeight: "100vh" }}
    >
      <Row className="blueBackground-old coursebnr quiz-details-hero">
        <Typography.Title
          level={1}
          className="fontFamily1 aboutbanner-content"
          style={{color:"#fff"}}
        >
          Bulk add questions
        </Typography.Title>
      </Row>

      <div className="boxDetails2 quiz-details-shell">
        <Row justify="center">
          <Col xs={24} md={20}>
            <Card className="quiz-details-top-card" bordered={false}>
              <div className="course-management-title-block">
                <button
                  type="button"
                  className="course-management-inline-back"
                  aria-label="Go back"
                  onClick={() => navigate(-1)}
                >
                  <FaArrowLeft aria-hidden="true" />
                </button>
                <Typography.Title
                  level={2}
                  className="fontFamily1 quiz-details-page-title"
                >
                  Add Questions in Bulk
                </Typography.Title>
              </div>
            </Card>

            <div className="quiz-details-panel" style={{ marginTop: 20 }}>
              <Typography.Paragraph type="secondary" className="quiz-details-bulk-intro">
                Add multiple questions at once. Each question must have a unique title. Types: MCQ (min 2 options),
                True/False, or Fill in the Blanks.
              </Typography.Paragraph>

              {questions.map((q, index) => (
                <Card
                  key={index}
                  size="small"
                  className="quiz-details-bulk-card"
                  title={`Question ${index + 1}`}
                  extra={
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeQuestion(index)}
                      disabled={questions.length === 1}
                    >
                      Remove
                    </Button>
                  }
                >
                  <Row style={{ padding: "20px 0", backgroundColor: "" }}>
                    <Col xs={24} md={18}>
                      <Row gutter={20}>
                        <Col xs={24} sm={12} {...colLabelProps}>
                          <Typography.Title className="fontFamily1" style={labelStyle}>
                            Type :
                          </Typography.Title>
                          <Select
                            value={q.type}
                            onChange={(v) => updateQuestion(index, "type", v)}
                            style={{ width: "100%" }}
                            className="signupSelectBox"
                            // size="large"
                            options={QUESTION_TYPES}
                          />
                        </Col>
                        <Col xs={24} sm={12} {...colLabelProps}>
                          <Typography.Title className="fontFamily1" style={labelStyle}>
                            Score :
                          </Typography.Title>
                          <InputNumber
                            min={0}
                            max={100}
                            value={q.score}
                            onChange={(v) => updateQuestion(index, "score", v)}
                            style={{ width: "100%" }}
                            className="signupFormInput"
                            size="large"
                            placeholder="Enter Score"
                          />
                        </Col>
                        <Col xs={24} {...colLabelProps}>
                          <Typography.Title className="fontFamily1" style={labelStyle}>
                            Title (question text) :
                          </Typography.Title>
                          <Input
                            value={q.title}
                            onChange={(e) => updateQuestion(index, "title", e.target.value)}
                            placeholder="Enter Question"
                            className="signupFormInput"
                            size="large"
                          />
                        </Col>

                        {q.type === "MCQ" && (
                          <>
                            {["option1", "option2", "option3", "option4"].map((key, i) => (
                              <Col xs={24} sm={12} key={key} {...colLabelProps}>
                                <Typography.Title className="fontFamily1" style={labelStyle}>
                                  Option {i + 1} :
                                </Typography.Title>
                                <Input
                                  value={q[key]}
                                  onChange={(e) => updateQuestion(index, key, e.target.value)}
                                  placeholder="Enter Option"
                                  className="signupFormInput"
                                  size="large"
                                />
                              </Col>
                            ))}
                            <Col xs={24} sm={12} {...colLabelProps}>
                              <Typography.Title className="fontFamily1" style={labelStyle}>
                                Correct Option :
                              </Typography.Title>
                              <Select
                                value={q.correctOptionKey}
                                onChange={(v) => updateQuestion(index, "correctOptionKey", v)}
                                style={{ width: "100%" }}
                                className="signupSelectBox"
                                size="large"
                                placeholder="Select Option"
                              >
                                <Option value="option1">Option 1</Option>
                                <Option value="option2">Option 2</Option>
                                <Option value="option3">Option 3</Option>
                                <Option value="option4">Option 4</Option>
                              </Select>
                            </Col>
                          </>
                        )}

                        {q.type === "TRUE_FALSE" && (
                          <Col xs={24} sm={12} {...colLabelProps}>
                            <Typography.Title className="fontFamily1" style={labelStyle}>
                              Correct Option :
                            </Typography.Title>
                            <Select
                              value={q.correctOption}
                              onChange={(v) => updateQuestion(index, "correctOption", v)}
                              style={{ width: "100%" }}
                              className="signupSelectBox"
                              size="large"
                              placeholder="Select Option"
                            >
                              <Option value="true">True</Option>
                              <Option value="false">False</Option>
                            </Select>
                          </Col>
                        )}

                        {q.type === "FILL_IN_THE_BLANKS" && (
                          <Col xs={24} sm={12} {...colLabelProps}>
                            <Typography.Title className="fontFamily1" style={labelStyle}>
                              Correct Answer :
                            </Typography.Title>
                            <Input
                              value={q.correctAnswer}
                              onChange={(e) => updateQuestion(index, "correctAnswer", e.target.value)}
                              placeholder="Correct answer"
                              className="signupFormInput"
                              size="large"
                            />
                          </Col>
                        )}
                      </Row>
                    </Col>
                  </Row>
                </Card>
              ))}

              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addQuestion}
                style={{ marginBottom: 24 }}
              >
                Add another question
              </Button>

              <div className="quiz-details-form-actions" style={{ marginTop: 24 }}>
                <Button
                  type="primary"
                  size="large"
                  className="loginButton"
                  loading={loading}
                  onClick={handleSubmit}
                >
                  {loading ? "Submitting..." : "Submit all questions"}
                </Button>
                <Button
                  onClick={() => navigate(-1)}
                  className="fontFamily1 quiz-details-cancel"
                  disabled={loading}
                  size="large"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </Layout>
  );
}

export default QuestionAddBulk;
